<template>
  <div>
    <div id="title-panel">
      <div id="title-container">
        <h1>{{ $t('suppliersList.suppliers') }}</h1>
        <div id="suppliers-counter">{{ pageOptions.rows }}</div>
      </div>
      <p id="description">
        {{ $t('suppliersList.description') }}
      </p>
    </div>

    <table-wrapper
      v-model:filters="filters"
      v-model:page-options="pageOptions"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="suppliers"
      :loading="isLoading"
      :mode="TableMode.LAZY"
      row-clickable
      @row-click="handleRowClick"
    >
    </table-wrapper>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { SuppliersService } from '@/services';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '@/components/TableWrapper';
  import { ISuppliersSummaryDto, ISuppliersSummaryPageOptionsDto } from 'types';
  import { routeParams, Route } from '@/routes';
  import { useRouter } from 'vue-router';

  const { t } = useI18n();
  const suppliers = ref<ISuppliersSummaryDto[]>([]);

  const TABLE_NAMESPACE = 'sl';
  const { pageOptions, filters, isLoading, filterParams } = useTableWrapper<
    ISuppliersSummaryDto,
    ISuppliersSummaryPageOptionsDto
  >(TableMode.LAZY, 'name', fetchSuppliers, undefined, true, TABLE_NAMESPACE);

  async function fetchSuppliers() {
    try {
      const pageDto = await SuppliersService.getSuppliersList({
        order: pageOptions.value.sortOrder,
        page: pageOptions.value.page,
        sortBy: pageOptions.value.sortBy,
        take: pageOptions.value.perPage,
        ...filterParams.value,
      });
      suppliers.value = pageDto.data;
      pageOptions.value.rows = pageDto.meta.itemCount;
    } catch {
      t('suppliersList.error.couldNotLoadSuppliers');
    }
  }

  const tableFields = computed(() =>
    new TableFieldsBuilder<ISuppliersSummaryDto>({
      sortable: true,
      labelTranslationKey: 'suppliersList',
    })
      .addField('name', {
        width: '50%',
      })
      .addField('SAPNumber', {
        width: '25%',
      })
      .addField('coworkersCount', {
        width: '25%',
        label: t('suppliersList.coworkers'),
      })
      .build(),
  );
  const filterConfiguration = computed(() =>
    new TableFiltersBuilder<
      ISuppliersSummaryDto,
      ISuppliersSummaryPageOptionsDto
    >({
      translationKey: 'suppliersList',
    })
      .addInputFilter('name', {
        width: '280px',
        simpleLabel: true,
      })
      .addInputFilter('SAPNumber', {
        width: '208px',
        simpleLabel: true,
      })
      .addNumberFilter('coworkersCount', {
        width: '120px',
        simpleLabel: true,
        customLabel: t('suppliersList.coworkers'),
      })
      .build(),
  );

  const router = useRouter();
  function handleRowClick(supplier: ISuppliersSummaryDto) {
    router.push({
      name: Route.SUPPLIER_DETAILS,
      params: {
        [routeParams[Route.SUPPLIER_DETAILS].supplierId]: supplier.id,
      },
    });
  }
</script>

<style scoped lang="scss">
  #title-panel {
    margin-bottom: 50px;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 2fr 1fr;
    align-items: start;
    justify-items: center;

    #title-container {
      justify-self: start;
      display: flex;
      align-items: flex-start;

      h1 {
        font-size: 50px;
        font-weight: 700;
      }
      #suppliers-counter {
        background-color: black;
        color: white;
        font-size: 12px;
        border-radius: 50px;
        padding: 3px 20px;
      }
    }

    p#description {
      margin-bottom: 0;
      max-height: 62px;
      width: 645px;
      font-size: 16px;
      line-height: 28px;
      font-weight: 500;
    }

    button#add-project-button {
      justify-self: end;
    }
  }
</style>
